import * as R from 'remeda'

import {
  type SurveyGroupCustomerAttributeFilterBrandSegments,
  type SurveyGroupQuestion as SurveyGroupQuestionBase,
  type SurveyGroupQuestionFilter,
  type SurveyGroupSegmentFilter,
} from '~/gql/graphql'

import { compact, exists, filterExists, findByPath, pathOr } from '../generals'

import {
  type SurveyGroupFilterGroups,
  type SurveyGroupFilterItem,
} from './surveyGroupFilterGroups'

type SurveyGroupFilterV1 = {
  questionFilters: SurveyGroupQuestionFilter[] | undefined
  segmentFilters: SurveyGroupSegmentFilter[] | undefined
}

type SurveyGroupFilterV2 = SurveyGroupFilterGroups

type SurveyGroupQuestion = App.PickGraphQL<
  SurveyGroupQuestionBase,
  'answerOptions' | 'id'
>

/**
 * サーベイグループフィルター ver.1 からサーベイフィルター ver.2 に変換します。
 *
 * @param surveyGroupFilterV1 - サーベイグループフィルター ver.1 。
 * @param questions - 設問の配列。
 * @returns サーベイグループフィルター ver.2 。
 */
export function toSurveyGroupFilterV2(
  surveyGroupFilterV1: SurveyGroupFilterV1,
  questions: SurveyGroupQuestion[],
): SurveyGroupFilterV2 {
  const questionFilters = (
    surveyGroupFilterV1.questionFilters ?? []
  ).map<SurveyGroupFilterItem>(
    ({ operation, surveyGroupQuestionId, values }) => ({
      category: 'Question',
      brandSegments: null,
      customerAttribute: null,
      question: {
        surveyGroupQuestionId,
        operation,
        answerOptions: R.pipe(
          values ?? [],
          R.map((option) =>
            R.pipe(
              questions,
              findByPath('id', surveyGroupQuestionId),
              pathOr('answerOptions', []),
              filterExists,
              findByPath('option', option),
            ),
          ),
          compact,
          R.map(R.pick(['option', 'text'])),
        ),
      },
    }),
  )

  const brandSegmentsArray: SurveyGroupCustomerAttributeFilterBrandSegments[] =
    []
  for (const {
    segmentNumber,
    surveyGroupBrandId,
  } of surveyGroupFilterV1.segmentFilters ?? []) {
    const brandSegments = findByPath(
      brandSegmentsArray,
      'surveyGroupBrandId',
      surveyGroupBrandId,
    )
    if (exists(brandSegments)) {
      brandSegments.segmentNumbers.push(segmentNumber)
    } else {
      brandSegmentsArray.push({
        surveyGroupBrandId: surveyGroupBrandId,
        segmentNumbers: [segmentNumber],
      })
    }
  }

  const brandSegmentsFilters = brandSegmentsArray.map<SurveyGroupFilterItem>(
    (brandSegments) => ({
      category: 'BrandSegments',
      brandSegments,
      customerAttribute: null,
      question: null,
    }),
  )

  return brandSegmentsFilters.length === 0
    ? questionFilters.length === 0
      ? []
      : [questionFilters]
    : brandSegmentsFilters.map((brandSegmentFilter) => [
        ...questionFilters,
        brandSegmentFilter,
      ])
}
